/**
 * G16 — Plan change history panel. Renders below the current Plan card
 * so admins can see "we upgraded Jan, downgraded Jul, request pending
 * again today" in a single glance.
 */

import { Card } from '@centraoneplus/ui';
import { PlanChangeRequest, usePlanHistory } from './hooks';

const STATUS_CHIP: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-700 dark:text-gray-200',
};

export function PlanHistory({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = usePlanHistory(tenantId);

  if (isLoading) {
    return (
      <Card className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading plan history…</Card>
    );
  }

  const history: PlanChangeRequest[] = data ?? [];
  if (history.length === 0) {
    return (
      <Card className="p-4 text-sm text-gray-500 dark:text-gray-400">
        No plan changes recorded yet.
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Plan history
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Full record of plan change requests — who asked, who approved,
          what was decided.
        </p>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:text-gray-400">
          <tr>
            <th className="text-left px-4 py-2">When</th>
            <th className="text-left px-4 py-2">From → To</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Contract / Notes</th>
          </tr>
        </thead>
        <tbody>
          {history.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="px-4 py-2 text-gray-700 dark:text-gray-200 whitespace-nowrap">
                {new Date(row.requestedAt).toLocaleString()}
              </td>
              <td className="px-4 py-2 capitalize">
                {row.fromPlan} → {row.toPlan}
              </td>
              <td className="px-4 py-2">
                <span
                  className={`inline-block px-2 py-0.5 text-xs rounded ${
                    STATUS_CHIP[row.status] ?? STATUS_CHIP.CANCELLED
                  }`}
                >
                  {row.status}
                </span>
                {row.status === 'REJECTED' && row.rejectedReason && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {row.rejectedReason}
                  </div>
                )}
              </td>
              <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                {row.contractRef && (
                  <div>
                    <span className="font-medium">Ref:</span> {row.contractRef}
                  </div>
                )}
                {row.notes && (
                  <div className="text-gray-500 dark:text-gray-400 line-clamp-2">{row.notes}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
